/**
 * Zod schemas for AI-proposed graph mutations.
 *
 * Mirrors the TypeScript types in ../types/graph-proposals.ts.
 * Used by the backend to validate tool_call arguments returned by the LLM.
 */

import { z } from 'zod';

// ─── Relationship type enum ───────────────────────────────────────────────────

/** Canonical relationship types — must stay in sync with RelationshipType in types/ontology.ts. */
const RelationshipTypeSchema = z.enum([
  'is-a',
  'part-of',
  'has-property',
  'relates-to',
  'depends-on',
  'implements',
  'extends',
  'uses',
  'contains',
  'produces',
  'consumes',
  'custom',
]);

// ─── Proposal schemas ─────────────────────────────────────────────────────────

const NodePropertySchema = z.object({
  name: z.string().min(1),
  value: z.string().optional(),
});

export const AddNodeProposalSchema = z.object({
  kind: z.literal('add_node'),
  label: z.string().min(1),
  description: z.string().optional(),
  properties: z.array(NodePropertySchema).optional(),
  rationale: z.string().optional(),
});

export const AddRelationProposalSchema = z.object({
  kind: z.literal('add_relation'),
  sourceNodeId: z.string().min(1),
  sourceNodeLabel: z.string().min(1),
  targetNodeId: z.string().min(1),
  targetNodeLabel: z.string().min(1),
  relationshipType: RelationshipTypeSchema,
  label: z.string().min(1),
  rationale: z.string().optional(),
});

export const EditNodeProposalSchema = z.object({
  kind: z.literal('edit_node'),
  nodeId: z.string().min(1),
  currentLabel: z.string().min(1),
  label: z.string().min(1).optional(),
  description: z.string().optional(),
  properties: z.array(NodePropertySchema).optional(),
  rationale: z.string().optional(),
});

// ─── Union ────────────────────────────────────────────────────────────────────

export const GraphProposalSchema = z.discriminatedUnion('kind', [
  AddNodeProposalSchema,
  AddRelationProposalSchema,
  EditNodeProposalSchema,
]);

// ─── Inferred types (for consumers that want Zod-inferred types) ──────────────

export type AddNodeProposalInput = z.infer<typeof AddNodeProposalSchema>;
export type AddRelationProposalInput = z.infer<typeof AddRelationProposalSchema>;
export type EditNodeProposalInput = z.infer<typeof EditNodeProposalSchema>;
export type GraphProposalInput = z.infer<typeof GraphProposalSchema>;

// Made with Bob
